import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCrypto } from '../context/CryptoContext';
import CoinSelector from '../components/CoinSelector';
import {
    LuPlus,
    LuTrash2,
    LuRefreshCw,
    LuTrendingUp,
    LuTrendingDown,
    LuScale,
    LuChartColumn,
    LuDollarSign,
    LuPercent,
    LuCalendarDays,
    LuZap,
    LuActivity,
    LuCoins
} from 'react-icons/lu';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    RadialLinearScale
} from 'chart.js';
import { Line, Bar, Radar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, RadialLinearScale);

const COLORS = ['#00d4ff', '#00ff88', '#a855f7', '#f59e0b', '#ef4444'];

function ComparisonTool({ mode = 'fiat' }) {
    const { lang } = useLanguage();
    const { coins, connected } = useCrypto();
    const isCrypto = mode === 'crypto';

    // Fiat mode scenarios
    const [scenarios, setScenarios] = useState([
        { id: 1, name: lang === 'tr' ? 'Senaryo 1' : 'Scenario 1', balance: 10000, dailyRate: 1, days: 30, color: COLORS[0] },
        { id: 2, name: lang === 'tr' ? 'Senaryo 2' : 'Scenario 2', balance: 10000, dailyRate: 1.5, days: 30, color: COLORS[1] }
    ]);

    // Crypto mode - selected coins for comparison
    const [selectedCoins, setSelectedCoins] = useState(['BTCUSDT', 'ETHUSDT']);
    const [investmentAmount, setInvestmentAmount] = useState(1000);
    const [cryptoResults, setCryptoResults] = useState(null);

    const [results, setResults] = useState(null);

    // Fiat mode functions
    const addScenario = () => {
        if (scenarios.length >= 5) return;
        const newId = Math.max(...scenarios.map(s => s.id)) + 1;
        setScenarios([...scenarios, {
            id: newId,
            name: (lang === 'tr' ? 'Senaryo ' : 'Scenario ') + newId,
            balance: 10000,
            dailyRate: 1,
            days: 30,
            color: COLORS[scenarios.length % COLORS.length]
        }]);
    };

    const removeScenario = (id) => {
        if (scenarios.length <= 2) return;
        setScenarios(scenarios.filter(s => s.id !== id));
    };

    const updateScenario = (id, field, value) => {
        setScenarios(scenarios.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const calculateResults = () => {
        const calculated = scenarios.map(scenario => {
            const { balance, dailyRate, days } = scenario;
            const dailyData = [];
            let currentBalance = parseFloat(balance) || 0;
            const rate = (parseFloat(dailyRate) || 0) / 100;

            for (let day = 0; day <= days; day++) {
                dailyData.push({ day, balance: currentBalance });
                currentBalance *= (1 + rate);
            }

            const finalBalance = dailyData[dailyData.length - 1].balance;
            const totalProfit = finalBalance - parseFloat(balance);
            const totalReturn = ((finalBalance - parseFloat(balance)) / parseFloat(balance)) * 100;
            const avgDaily = totalProfit / days;

            return {
                ...scenario,
                dailyData,
                finalBalance,
                totalProfit,
                totalReturn,
                avgDaily
            };
        });

        setResults(calculated);
    };

    // Crypto mode functions
    const addCoinToCompare = () => {
        if (selectedCoins.length >= 5) return;
        const availableCoins = coins.filter(c => !selectedCoins.includes(c.symbol));
        if (availableCoins.length > 0) {
            setSelectedCoins([...selectedCoins, availableCoins[0].symbol]);
        }
    };

    const removeCoinFromCompare = (symbol) => {
        if (selectedCoins.length <= 2) return;
        setSelectedCoins(selectedCoins.filter(s => s !== symbol));
    };

    const updateCoinSelection = (index, newSymbol) => {
        const updated = [...selectedCoins];
        updated[index] = newSymbol;
        setSelectedCoins(updated);
    };

    const calculateCryptoComparison = () => {
        const comparisonData = selectedCoins.map((symbol, index) => {
            const coin = coins.find(c => c.symbol === symbol);
            if (!coin) return null;

            const amount = investmentAmount / coin.price;
            const currentValue = amount * coin.price;

            // Calculate 24h volatility
            const volatility = coin.highPrice && coin.lowPrice && coin.price > 0
                ? ((coin.highPrice - coin.lowPrice) / coin.price) * 100
                : 5;

            // Simulated daily return based on 24h change
            const dailyReturn = coin.priceChangePercent || 0;

            // Project 30 day returns (simple projection based on current trend)
            const projectedValue30d = currentValue * Math.pow(1 + dailyReturn / 100, 30);
            const projectedProfit30d = projectedValue30d - currentValue;

            // Volume score
            const volumeScore = Math.min(100, (coin.quoteVolume / 1000000000) * 10);

            // Risk score (based on volatility)
            const riskScore = Math.min(100, volatility * 10);

            return {
                symbol: coin.symbol,
                shortName: coin.shortName,
                name: coin.name,
                price: coin.price,
                priceChange24h: coin.priceChangePercent || 0,
                highPrice: coin.highPrice,
                lowPrice: coin.lowPrice,
                volume: coin.quoteVolume,
                volatility,
                amount,
                currentValue,
                dailyReturn,
                projectedValue30d,
                projectedProfit30d,
                volumeScore,
                riskScore,
                color: COLORS[index % COLORS.length]
            };
        }).filter(Boolean);

        setCryptoResults(comparisonData);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}%`;
    };

    const formatVolume = (volume) => {
        if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
        if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
        if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
        return `$${volume.toFixed(2)}`;
    };

    const getCoinIcon = (shortName) => {
        return `https://assets.coincap.io/assets/icons/${shortName?.toLowerCase()}@2x.png`;
    };

    // Crypto charts
    const performanceChartData = cryptoResults ? {
        labels: cryptoResults.map(c => c.shortName),
        datasets: [{
            label: lang === 'tr' ? '24s Değişim (%)' : '24h Change (%)',
            data: cryptoResults.map(c => c.priceChange24h),
            backgroundColor: cryptoResults.map(c => c.priceChange24h >= 0 ? 'rgba(16, 185, 129, 0.7)' : 'rgba(239, 68, 68, 0.7)'),
            borderRadius: 8
        }]
    } : null;

    const projectionChartData = cryptoResults ? {
        labels: cryptoResults.map(c => c.shortName),
        datasets: [{
            label: lang === 'tr' ? 'Şu anki Değer' : 'Current Value',
            data: cryptoResults.map(c => c.currentValue),
            backgroundColor: 'rgba(0, 212, 255, 0.7)',
            borderRadius: 8
        }, {
            label: lang === 'tr' ? '30 Gün Projeksiyon' : '30 Day Projection',
            data: cryptoResults.map(c => c.projectedValue30d),
            backgroundColor: 'rgba(0, 255, 136, 0.7)',
            borderRadius: 8
        }]
    } : null;

    const radarChartData = cryptoResults ? {
        labels: [
            lang === 'tr' ? 'Performans' : 'Performance',
            lang === 'tr' ? 'Likidite' : 'Liquidity',
            lang === 'tr' ? 'Stabilite' : 'Stability',
            lang === 'tr' ? 'Hacim' : 'Volume',
            lang === 'tr' ? 'Potansiyel' : 'Potential'
        ],
        datasets: cryptoResults.map(c => ({
            label: c.shortName,
            data: [
                Math.min(100, Math.max(0, 50 + c.priceChange24h * 5)),
                c.volumeScore,
                Math.max(0, 100 - c.riskScore),
                c.volumeScore,
                Math.min(100, Math.max(0, 50 + c.priceChange24h * 3))
            ],
            backgroundColor: c.color + '30',
            borderColor: c.color,
            borderWidth: 2
        }))
    } : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { color: '#9ca3af' } } },
        scales: {
            x: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af' } },
            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af' } }
        }
    };

    const radarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { color: '#9ca3af' } } },
        scales: {
            r: {
                grid: { color: 'rgba(255,255,255,0.1)' },
                ticks: { display: false },
                pointLabels: { color: '#9ca3af' }
            }
        }
    };

    // Crypto Mode UI
    if (isCrypto) {
        return (
            <div className="comparison-page crypto-mode">
                <div className="page-header home-header">
                    <div className="page-header-content">
                        <div className="page-title-group">
                            <div>
                                <h1 className="page-title">{lang === 'tr' ? 'Coin Karşılaştırma' : 'Coin Comparison'}</h1>
                            </div>
                        </div>
                        {connected && (
                            <div className="page-actions">
                                <span className="live-indicator">
                                    <LuZap className="live-icon" />
                                    <span>{lang === 'tr' ? 'Canlı Veri' : 'Live Data'}</span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="comparison-content crypto-comparison-content">
                    {/* Investment Amount */}
                    <div className="card investment-input-card">
                        <div className="investment-header">
                            <h3><LuCoins /> {lang === 'tr' ? 'Yatırım Miktarı' : 'Investment Amount'}</h3>
                            <p>{lang === 'tr' ? 'Her coine eşit miktarda yatırım yapıldığını varsayarak karşılaştır' : 'Compare assuming equal investment in each coin'}</p>
                        </div>
                        <div className="investment-input-row">
                            <div className="form-group">
                                <label><LuDollarSign /> USD</label>
                                <input
                                    type="number"
                                    className="form-input investment-input"
                                    value={investmentAmount}
                                    onChange={(e) => setInvestmentAmount(parseFloat(e.target.value) || 0)}
                                    placeholder="1000"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Coin Selection */}
                    <div className="coin-selection-grid">
                        {selectedCoins.map((symbol, index) => {
                            const coin = coins.find(c => c.symbol === symbol);
                            return (
                                <div key={index} className="coin-compare-card" style={{ '--coin-color': COLORS[index % COLORS.length] }}>
                                    <div className="coin-compare-header">
                                        <span className="coin-index">{index + 1}</span>
                                        {selectedCoins.length > 2 && (
                                            <button className="btn-icon btn-remove-sm" onClick={() => removeCoinFromCompare(symbol)}>
                                                <LuTrash2 />
                                            </button>
                                        )}
                                    </div>
                                    <CoinSelector
                                        coins={coins}
                                        selectedCoin={symbol}
                                        onSelect={(newSymbol) => updateCoinSelection(index, newSymbol)}
                                        lang={lang}
                                    />
                                    {coin && (
                                        <div className="coin-quick-stats">
                                            <div className="quick-stat">
                                                <span className="stat-label">{lang === 'tr' ? 'Fiyat' : 'Price'}</span>
                                                <span className="stat-value">{formatCurrency(coin.price)}</span>
                                            </div>
                                            <div className="quick-stat">
                                                <span className="stat-label">24h</span>
                                                <span className={`stat-value ${(coin.priceChangePercent || 0) >= 0 ? 'positive' : 'negative'}`}>
                                                    {formatPercent(coin.priceChangePercent || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {selectedCoins.length < 5 && (
                            <button className="add-coin-btn" onClick={addCoinToCompare}>
                                <LuPlus />
                                <span>{lang === 'tr' ? 'Coin Ekle' : 'Add Coin'}</span>
                            </button>
                        )}
                    </div>

                    <div className="comparison-actions">
                        <button className="btn btn-primary btn-lg" onClick={calculateCryptoComparison}>
                            <LuRefreshCw /> {lang === 'tr' ? 'Karşılaştır' : 'Compare'}
                        </button>
                    </div>

                    {/* Results */}
                    {cryptoResults && cryptoResults.length > 0 && (
                        <>
                            {/* Comparison Table */}
                            <div className="card comparison-table-card">
                                <h3>{lang === 'tr' ? 'Detaylı Karşılaştırma' : 'Detailed Comparison'}</h3>
                                <div className="comparison-table-wrapper">
                                    <table className="comparison-table">
                                        <thead>
                                            <tr>
                                                <th>{lang === 'tr' ? 'Coin' : 'Coin'}</th>
                                                <th>{lang === 'tr' ? 'Fiyat' : 'Price'}</th>
                                                <th>24h %</th>
                                                <th>{lang === 'tr' ? 'Alınan Miktar' : 'Amount Bought'}</th>
                                                <th>{lang === 'tr' ? 'Değer' : 'Value'}</th>
                                                <th>{lang === 'tr' ? 'Volatilite' : 'Volatility'}</th>
                                                <th>{lang === 'tr' ? 'Hacim' : 'Volume'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cryptoResults.map((coin, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="table-coin-cell">
                                                            <span className="color-indicator" style={{ background: coin.color }}></span>
                                                            <img src={getCoinIcon(coin.shortName)} alt={coin.shortName} className="coin-mini-icon" onError={(e) => e.target.style.display = 'none'} />
                                                            <span className="coin-name">{coin.shortName}</span>
                                                        </div>
                                                    </td>
                                                    <td>{formatCurrency(coin.price)}</td>
                                                    <td className={coin.priceChange24h >= 0 ? 'positive' : 'negative'}>
                                                        {coin.priceChange24h >= 0 ? <LuTrendingUp /> : <LuTrendingDown />}
                                                        {formatPercent(coin.priceChange24h)}
                                                    </td>
                                                    <td>{coin.amount.toFixed(6)}</td>
                                                    <td>{formatCurrency(coin.currentValue)}</td>
                                                    <td className={coin.volatility > 10 ? 'high-risk' : ''}>{coin.volatility.toFixed(2)}%</td>
                                                    <td>{formatVolume(coin.volume)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Charts Grid */}
                            <div className="crypto-comparison-charts">
                                <div className="card chart-card">
                                    <h3><LuActivity /> {lang === 'tr' ? '24s Performans' : '24h Performance'}</h3>
                                    <div className="chart-container" style={{ height: '250px' }}>
                                        <Bar data={performanceChartData} options={chartOptions} />
                                    </div>
                                </div>

                                <div className="card chart-card">
                                    <h3><LuTrendingUp /> {lang === 'tr' ? 'Değer Projeksiyonu' : 'Value Projection'}</h3>
                                    <div className="chart-container" style={{ height: '250px' }}>
                                        <Bar data={projectionChartData} options={chartOptions} />
                                    </div>
                                </div>

                                <div className="card chart-card radar-chart-card">
                                    <h3><LuScale /> {lang === 'tr' ? 'Metrik Karşılaştırma' : 'Metric Comparison'}</h3>
                                    <div className="chart-container" style={{ height: '300px' }}>
                                        <Radar data={radarChartData} options={radarOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Winner */}
                            <div className="crypto-winner-section">
                                <div className="winner-cards">
                                    <div className="winner-card best-performance">
                                        <span className="winner-label">{lang === 'tr' ? '24s En İyi Performans' : '24h Best Performance'}</span>
                                        <div className="winner-coin">
                                            <img src={getCoinIcon(cryptoResults.sort((a, b) => b.priceChange24h - a.priceChange24h)[0].shortName)} alt="" className="winner-icon" onError={(e) => e.target.style.display = 'none'} />
                                            <h3>{cryptoResults.sort((a, b) => b.priceChange24h - a.priceChange24h)[0].shortName}</h3>
                                        </div>
                                        <span className="winner-value positive">
                                            {formatPercent(cryptoResults.sort((a, b) => b.priceChange24h - a.priceChange24h)[0].priceChange24h)}
                                        </span>
                                    </div>

                                    <div className="winner-card highest-volume">
                                        <span className="winner-label">{lang === 'tr' ? 'En Yüksek Hacim' : 'Highest Volume'}</span>
                                        <div className="winner-coin">
                                            <img src={getCoinIcon(cryptoResults.sort((a, b) => b.volume - a.volume)[0].shortName)} alt="" className="winner-icon" onError={(e) => e.target.style.display = 'none'} />
                                            <h3>{cryptoResults.sort((a, b) => b.volume - a.volume)[0].shortName}</h3>
                                        </div>
                                        <span className="winner-value">
                                            {formatVolume(cryptoResults.sort((a, b) => b.volume - a.volume)[0].volume)}
                                        </span>
                                    </div>

                                    <div className="winner-card lowest-risk">
                                        <span className="winner-label">{lang === 'tr' ? 'En Düşük Volatilite' : 'Lowest Volatility'}</span>
                                        <div className="winner-coin">
                                            <img src={getCoinIcon(cryptoResults.sort((a, b) => a.volatility - b.volatility)[0].shortName)} alt="" className="winner-icon" onError={(e) => e.target.style.display = 'none'} />
                                            <h3>{cryptoResults.sort((a, b) => a.volatility - b.volatility)[0].shortName}</h3>
                                        </div>
                                        <span className="winner-value">
                                            {cryptoResults.sort((a, b) => a.volatility - b.volatility)[0].volatility.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Fiat Mode UI (original)
    const maxDays = results ? Math.max(...results.map(r => r.days)) : 30;

    const lineChartData = results ? {
        labels: Array.from({ length: maxDays + 1 }, (_, i) => i),
        datasets: results.map(r => ({
            label: r.name,
            data: r.dailyData.map(d => d.balance),
            borderColor: r.color,
            backgroundColor: r.color + '20',
            fill: false,
            tension: 0.4
        }))
    } : null;

    const barChartData = results ? {
        labels: results.map(r => r.name),
        datasets: [{
            label: lang === 'tr' ? 'Toplam Kar' : 'Total Profit',
            data: results.map(r => r.totalProfit),
            backgroundColor: results.map(r => r.color),
            borderRadius: 8
        }]
    } : null;

    const fiatChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { color: '#9ca3af' } } },
        scales: {
            x: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af' } },
            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af', callback: (v) => formatCurrency(v) } }
        }
    };

    return (
        <div className="comparison-page">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">{lang === 'tr' ? 'Senaryo Karşılaştırma' : 'Scenario Comparison'}</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="comparison-content">
                {/* Scenario Cards */}
                <div className="scenarios-container">
                    {scenarios.map((scenario, index) => (
                        <div key={scenario.id} className="scenario-input-card" style={{ '--scenario-color': scenario.color }}>
                            <div className="scenario-header">
                                <input type="text" className="scenario-name-input" value={scenario.name} onChange={(e) => updateScenario(scenario.id, 'name', e.target.value)} />
                                {scenarios.length > 2 && (
                                    <button className="btn-icon danger" onClick={() => removeScenario(scenario.id)}><LuTrash2 /></button>
                                )}
                            </div>
                            <div className="scenario-fields">
                                <div className="field">
                                    <label><LuDollarSign /> {lang === 'tr' ? 'Baslangic' : 'Initial'}</label>
                                    <input type="number" value={scenario.balance} onChange={(e) => updateScenario(scenario.id, 'balance', e.target.value)} />
                                </div>
                                <div className="field">
                                    <label><LuPercent /> {lang === 'tr' ? 'Gunluk %' : 'Daily %'}</label>
                                    <input type="number" step="0.1" value={scenario.dailyRate} onChange={(e) => updateScenario(scenario.id, 'dailyRate', e.target.value)} />
                                </div>
                                <div className="field">
                                    <label><LuCalendarDays /> {lang === 'tr' ? 'Gun' : 'Days'}</label>
                                    <input type="number" value={scenario.days} onChange={(e) => updateScenario(scenario.id, 'days', parseInt(e.target.value) || 0)} />
                                </div>
                            </div>
                        </div>
                    ))}

                    {scenarios.length < 5 && (
                        <button className="add-scenario-btn" onClick={addScenario}>
                            <LuPlus /> {lang === 'tr' ? 'Senaryo Ekle' : 'Add Scenario'}
                        </button>
                    )}
                </div>

                <div className="comparison-actions">
                    <button className="btn btn-primary btn-lg" onClick={calculateResults}>
                        <LuRefreshCw /> {lang === 'tr' ? 'Karsilastir' : 'Compare'}
                    </button>
                </div>

                {/* Results */}
                {results && (
                    <>
                        {/* Summary Cards */}
                        <div className="comparison-summary">
                            {results.map(r => (
                                <div key={r.id} className="result-summary-card" style={{ '--scenario-color': r.color }}>
                                    <div className="result-header">
                                        <span className="color-dot"></span>
                                        <h4>{r.name}</h4>
                                    </div>
                                    <div className="result-stats">
                                        <div className="stat">
                                            <span className="label">{lang === 'tr' ? 'Son Bakiye' : 'Final'}</span>
                                            <span className="value">{formatCurrency(r.finalBalance)}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="label">{lang === 'tr' ? 'Kar' : 'Profit'}</span>
                                            <span className="value profit">+{formatCurrency(r.totalProfit)}</span>
                                        </div>
                                        <div className="stat">
                                            <span className="label">{lang === 'tr' ? 'Getiri' : 'Return'}</span>
                                            <span className="value">{r.totalReturn.toFixed(1)}%</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Charts */}
                        <div className="comparison-charts">
                            <div className="card chart-card">
                                <h3><LuTrendingUp /> {lang === 'tr' ? 'Bakiye Gelisimi' : 'Balance Growth'}</h3>
                                <div className="chart-container" style={{ height: '300px' }}>
                                    <Line data={lineChartData} options={fiatChartOptions} />
                                </div>
                            </div>
                            <div className="card chart-card">
                                <h3><LuChartColumn /> {lang === 'tr' ? 'Kar Karsilastirmasi' : 'Profit Comparison'}</h3>
                                <div className="chart-container" style={{ height: '300px' }}>
                                    <Bar data={barChartData} options={fiatChartOptions} />
                                </div>
                            </div>
                        </div>

                        {/* Winner */}
                        <div className="comparison-winner">
                            <div className="winner-card">
                                <span className="winner-label">{lang === 'tr' ? 'En Iyi Senaryo' : 'Best Scenario'}</span>
                                <h3 style={{ color: results.sort((a, b) => b.totalProfit - a.totalProfit)[0].color }}>
                                    {results.sort((a, b) => b.totalProfit - a.totalProfit)[0].name}
                                </h3>
                                <span className="winner-profit">+{formatCurrency(results.sort((a, b) => b.totalProfit - a.totalProfit)[0].totalProfit)}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ComparisonTool;
